// File: client/src/components/rules/WorkflowConfigurationManager.js
import React, { useState } from 'react';
import { Button, Select, Space, Tooltip, Modal, Input, Dropdown, Menu, message } from 'antd';
import {
    SettingOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    DownOutlined,
    ExportOutlined,
    ImportOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import { Typography } from 'antd';

const { Text } = Typography;
const { Option } = Select;

const WorkflowConfigurationManager = ({
                                          workflows = [],
                                          currentWorkflow,
                                          onWorkflowChange,
                                          onCreateWorkflow,
                                          onDeleteWorkflow,
                                          onSaveWorkflow,
                                          hasUnsavedChanges
                                      }) => {
    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0); // Used to reset file input

    // Handle renaming workflow
    const handleRename = () => {
        if (!currentWorkflow) return;
        setNewName(currentWorkflow.name || '');
        setIsRenaming(true);
    };

    // Submit rename
    const submitRename = () => {
        if (!newName.trim()) {
            message.error('Workflow name cannot be empty');
            return;
        }

        const updatedWorkflow = {
            ...currentWorkflow,
            name: newName.trim()
        };

        onWorkflowChange(updatedWorkflow.id);
        onSaveWorkflow(); // Save after rename
        setIsRenaming(false);
    };

    // Handle duplicate workflow
    const handleDuplicate = () => {
        if (!currentWorkflow) return;

        // Create a duplicate workflow
        const duplicatedWorkflow = {
            ...currentWorkflow,
            name: `${currentWorkflow.name} (Copy)`,
            id: undefined, // Remove ID to create new
            isNew: true
        };

        // Call parent to create new workflow
        onWorkflowChange(duplicatedWorkflow);
    };

    // Handle export workflow
    const handleExport = () => {
        if (!currentWorkflow) return;

        try {
            // Create JSON file
            const workflowJson = JSON.stringify(currentWorkflow, null, 2);
            const blob = new Blob([workflowJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement('a');
            link.href = url;
            link.download = `${currentWorkflow.name.replace(/\s+/g, '_')}_workflow.json`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success('Workflow exported successfully');
        } catch (error) {
            console.error('Error exporting workflow:', error);
            message.error('Failed to export workflow');
        }
    };

    // Handle import workflow
    const handleImportClick = () => {
        // Reset file input and show file dialog
        setFileInputKey(prev => prev + 1);
        const fileInput = document.getElementById('import-workflow-file');
        if (fileInput) {
            fileInput.click();
        }
    };

    // Process imported workflow file
    const handleImportFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const importedWorkflow = JSON.parse(e.target.result);

                // Validate imported workflow
                if (!importedWorkflow.name) {
                    throw new Error('Invalid workflow format');
                }

                // Create new workflow from imported data
                const newWorkflow = {
                    ...importedWorkflow,
                    id: undefined, // Remove ID to create new
                    name: `${importedWorkflow.name} (Imported)`,
                    isNew: true
                };

                // Call parent to create new workflow
                onWorkflowChange(newWorkflow);
                message.success('Workflow imported successfully');
            } catch (error) {
                console.error('Error importing workflow:', error);
                message.error('Failed to import workflow: Invalid format');
            }
        };

        reader.readAsText(file);
    };

    // Ensure workflows is an array
    const workflowsArray = Array.isArray(workflows) ? workflows : [];

    // Dropdown menu for workflow actions
    const actionsMenu = (
        <Menu>
            <Menu.Item key="rename" icon={<EditOutlined />} onClick={handleRename}>
                Rename
            </Menu.Item>
            <Menu.Item key="duplicate" icon={<CopyOutlined />} onClick={handleDuplicate}>
                Duplicate
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="export" icon={<ExportOutlined />} onClick={handleExport}>
                Export Workflow
            </Menu.Item>
            <Menu.Item key="import" icon={<ImportOutlined />} onClick={handleImportClick}>
                Import Workflow
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                onClick={onDeleteWorkflow}
                danger
            >
                Delete Workflow
            </Menu.Item>
        </Menu>
    );

    return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Select
                    style={{ width: 240, marginRight: 12 }}
                    placeholder="Select Workflow"
                    value={currentWorkflow?.id}
                    onChange={onWorkflowChange}
                    disabled={isSaving}
                >
                    {workflowsArray.map(workflow => (
                        <Option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                        </Option>
                    ))}
                </Select>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onCreateWorkflow}
                    style={{ marginRight: 8 }}
                >
                    New Workflow
                </Button>

                <Dropdown overlay={actionsMenu} disabled={!currentWorkflow}>
                    <Button icon={<SettingOutlined />}>
                        Actions <DownOutlined />
                    </Button>
                </Dropdown>
            </div>

            <div>
                {hasUnsavedChanges && (
                    <>
                        <Text type="warning" style={{ marginRight: 8 }}>
                            Unsaved changes
                        </Text>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={onSaveWorkflow}
                            loading={isSaving}
                        >
                            Save
                        </Button>
                    </>
                )}
            </div>

            {/* Hidden file input for import */}
            <input
                id="import-workflow-file"
                type="file"
                key={fileInputKey}
                style={{ display: 'none' }}
                accept=".json"
                onChange={handleImportFile}
            />

            {/* Rename modal */}
            <Modal
                title="Rename Workflow"
                open={isRenaming}
                onOk={submitRename}
                onCancel={() => setIsRenaming(false)}
                okButtonProps={{ disabled: !newName.trim() }}
            >
                <Input
                    placeholder="Enter workflow name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    autoFocus
                />
            </Modal>
        </div>
    );
};

export default WorkflowConfigurationManager;
